// NOTE: the logic to perform manifest detection from the url is essentially
//       replicated in the Agent codebase. Ideally this logic should be kept
//       relatively synchronized. Please consider advising the Agent team
//       when/if the detection logic below is revised.
//
//       For reference, please see isManifestUrl() in the Agent codebase;
//       approximately around
//       https://github.com/kollective-services/node/blob/master/src/inv/SessionStore.cpp#L108
import { MimeType, SourceType } from '../constants';

function isHLSManifestUrl(url: string): boolean {
  try {
    const path = new URL(url).pathname.toLowerCase();
    return path.endsWith('.m3u8') || path.endsWith('.m3u');
  } catch (e) {
    // Only gets here if url string throws when creating a URL
    return false;
  }
}

function isDashManifestUrl(url: string): boolean {
  const lcurl = url.toLowerCase();
  return (
    lcurl.includes('.mpd') ||
    lcurl.includes('.ism/manifest(format=mpd-time-csf)')
  );
}

function isSmoothManifestUrl(url: string): boolean {
  const lcurl = url.toLowerCase();
  return lcurl.includes('.ism/manifest');
}

function parseExtension(url: string | undefined | null): SourceType | null {
  if (url) {
    if (isHLSManifestUrl(url)) {
      return SourceType.HLS;
    }
    if (isDashManifestUrl(url)) {
      return SourceType.DASH;
    }
    if (isSmoothManifestUrl(url)) {
      return SourceType.SMOOTH;
    }
    if (url.toLowerCase().includes('.mp4')) {
      return SourceType.MP4;
    }
  }
  return null;
}

export function findSourcesOfMimeType(
  mimeType: string,
  sources: Array<Record<string, string>>,
  urlKey: string,
  mimeKey: string
): Array<Record<string, string>> {
  if (!sources || !sources.length) {
    return [] as Array<Record<string, string>>;
  }
  return sources.reduce((data, source) => {
    const mime = source[mimeKey];
    const url = source[urlKey];
    if (getSourceTypeForMime(mime, url) == mimeType) {
      try {
        data.push(source);
      } catch (e) {
        // no-op
      }
    }
    return data;
  }, [] as Array<Record<string, string>>);
}

function compareMime(str: string, mime: string): boolean {
  return mime ? str.toLowerCase().includes(mime.toLowerCase()) : false;
}

function isSmoothMime(mime: string) {
  return compareMime(mime, MimeType.SMOOTH);
}

function isDashMime(mime: string) {
  return compareMime(mime, MimeType.DASH);
}

function isHLSMime(mime: string) {
  return [MimeType.HLS, MimeType.HLS_GENERAL].some(m => compareMime(mime, m));
}

function isMp4Mime(mime: string) {
  return compareMime(mime, MimeType.MP4);
}

export function getSourceTypeForMime(
  mime: string | undefined | null,
  url: string | undefined | null
): SourceType | null {
  if (!mime) {
    return parseExtension(url);
  }
  if (isSmoothMime(mime)) {
    return SourceType.SMOOTH;
  }
  if (isDashMime(mime)) {
    return SourceType.DASH;
  }
  if (isHLSMime(mime)) {
    return SourceType.HLS;
  }
  if (isMp4Mime(mime)) {
    return SourceType.MP4;
  }
  return parseExtension(url);
}

export function getMimeForSourceType(sourceType: string): string {
  switch (sourceType) {
    case SourceType.SMOOTH:
      return MimeType.SMOOTH;
    case SourceType.DASH:
      return MimeType.DASH;
    case SourceType.HLS:
      return MimeType.HLS;
    case SourceType.MP4:
      return MimeType.MP4;
    default:
      return '';
  }
}

export function isManifestMime(mime: string): boolean {
  return mime
    ? isDashMime(mime) || isSmoothMime(mime) || isHLSMime(mime)
    : false;
}

export function isManifestUrl(url: string): boolean {
  // SRV-1168 We shouldn't be considering an mp4 url as a manifest url
  return url
    ? isHLSManifestUrl(url) ||
        isDashManifestUrl(url) ||
        isSmoothManifestUrl(url)
    : false;
}
